import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import { hasAnyRole } from '../../constants/roles';
import { Spin } from 'antd';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Protected route component that requires authentication
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render if authenticated
 * @param {string[]} [props.requiredRoles] - Optional array of roles required to access the route
 * @returns {React.ReactNode} - Rendered component
 */
const ProtectedRoute = ({ children, requiredRoles }) => {
  const { isAuthenticated, user, isLoading: loading } = useAuthStore();
  const { initialized } = useAuth();
  const location = useLocation();
  
  // We'll use the hasAnyRole function from our roles constants

  // Show loading spinner while authentication state is being determined
  if (!initialized || loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        width: '100vw',
        maxWidth: '100%'
      }} role="status" aria-label="Loading authentication">
        <Spin size="large" />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Save the location the user was trying to access for redirect after login
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Check for required roles if specified
  if (requiredRoles && requiredRoles.length > 0) {
    const hasRequiredRole = hasAnyRole(user, requiredRoles);
    if (!hasRequiredRole) {
      // User doesn't have the required role, redirect to dashboard or unauthorized page
      return <Navigate to="/dashboard" replace />;
    }
  }

  // User is authenticated and has the required role(s), render the protected content
  return children;
};

export default ProtectedRoute;
